class StoreSelectorComponent extends HTMLElement {
  constructor() {
    super();

    this.handleTriggerClick = this.open.bind(this);
    this.handleSelectorClick = this.onSelectorClick.bind(this);
    this.handleCloseClick = this.close.bind(this);
    this.handleModalClick = this.onModalClick.bind(this);
    this.handleOptionClick = this.onOptionClick.bind(this);
    this.handleClearClick = this.onClearClick.bind(this);
    this.handleKeydown = this.onKeydown.bind(this);
    this.handleDocumentClick = this.onDocumentClick.bind(this);
    this.handleGlobalCloseClick = StoreSelectorComponent.onGlobalCloseClick.bind(StoreSelectorComponent);
    this.locations = [];
    this.userCoordinates = null;
    this.optionButtons = [];
    this.defaultLocationId = -1;
    this.hiddenLocationMap = this.getHiddenLocations();
  }

  connectedCallback() {
    this.cacheElements();
    StoreSelectorComponent.bindGlobalCloseListener();

    this.placeholder = this.dataset.placeholder || '';
    this.filterParam = this.dataset.filterParam || '';
    this.locationsEndpoint = this.dataset.locationsEndpoint || '';
    this.loadingText = this.dataset.loadingText || 'Loading stores...';
    this.errorText = this.dataset.errorText || 'Unable to load stores right now.';
    this.cartErrorText =
      this.dataset.cartErrorText || 'Some items in your cart cannot be fulfilled from that store.';
    this.emptyText = (this.statusEl?.textContent || '').trim() || 'No stores are available right now.';
    this.defaultLocationId = this.parseId(this.dataset.defaultLocationId ?? '-1');
    this.activeValue = this.dataset.activeValue || '';
    this.activeLabel = this.dataset.activeLabel || '';

    this.refreshOptionButtons();
    this.attachEventListeners();

    this.setActiveValue(this.activeValue, this.activeLabel);

    if (this.locationsEndpoint) {
      this.loadLocations();
    } else {
      this.syncWithInventorySelection();
    }
  }

  disconnectedCallback() {
    this.detachEventListeners();
    document.removeEventListener('keydown', this.handleKeydown);
    if (this.modalAttachedToBody && this.modal?.parentNode) {
      this.modal.parentNode.removeChild(this.modal);
    }
    document.removeEventListener('click', this.handleDocumentClick, true);
  }

  cacheElements() {
    this.trigger = this.querySelector('[data-store-selector-open]');
    this.modal = this.querySelector('[data-store-selector-modal]')
      || document.body.querySelector('[data-store-selector-modal][data-detached-from-host]');
    if (this.modal && !this.modal.dataset.detachedFromHost) {
      this.modal.dataset.detachedFromHost = 'true';
      document.body.appendChild(this.modal);
      this.modalAttachedToBody = true;
    }
    if (this.modal) {
      this.modal.__storeSelectorInstance = this;
      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');
    }
    this.dialog = this.modal ? this.modal.querySelector('.store-selector__dialog') : null;
    this.overlay = this.modal ? this.modal.querySelector('[data-store-selector-overlay]') : null;
    this.closeButtons = this.modal ? Array.from(this.modal.querySelectorAll('[data-store-selector-close]')) : [];
    this.optionsList = this.modal ? this.modal.querySelector('.store-selector__options') : null;
    this.statusEl = this.modal ? this.modal.querySelector('[data-store-selector-status]') : null;
    this.useLocationButton = this.modal ? this.modal.querySelector('[data-store-selector-use-location]') : null;
    this.clearButton = this.modal ? this.modal.querySelector('[data-store-selector-clear]') : null;
    this.valueTarget = this.querySelector('[data-store-selector-value]');
    this.searchInput = this.modal ? this.modal.querySelector('[data-store-selector-search]') : null;
  }

  attachEventListeners() {
    this.addEventListener('click', this.handleSelectorClick);
    this.modal?.addEventListener('click', this.handleModalClick);
    this.overlay?.addEventListener('click', this.handleCloseClick);
    this.closeButtons.forEach((button) => button.addEventListener('click', this.handleCloseClick));
    this.attachOptionListeners(this.optionButtons);
    this.clearButton?.addEventListener('click', this.handleClearClick);
    this.useLocationButton?.addEventListener('click', this.handleUseLocation);
    document.addEventListener('click', this.handleDocumentClick, true);
  }

  detachEventListeners() {
    this.removeEventListener('click', this.handleSelectorClick);
    this.modal?.removeEventListener('click', this.handleModalClick);
    this.overlay?.removeEventListener('click', this.handleCloseClick);
    this.closeButtons.forEach((button) => button.removeEventListener('click', this.handleCloseClick));
    this.detachOptionListeners();
    this.clearButton?.removeEventListener('click', this.handleClearClick);
    this.useLocationButton?.removeEventListener('click', this.handleUseLocation);
    document.removeEventListener('click', this.handleDocumentClick, true);
  }

  attachOptionListeners(buttons = []) {
    buttons.forEach((button) => button.addEventListener('click', this.handleOptionClick));
  }

  detachOptionListeners() {
    this.optionButtons.forEach((button) => button.removeEventListener('click', this.handleOptionClick));
  }

  refreshOptionButtons() {
    this.optionButtons = this.modal ? Array.from(this.modal.querySelectorAll('[data-store-option]')) : [];
  }

  handleUseLocation = () => {
    if (!navigator.geolocation) {
      console.warn('[StoreSelector] Geolocation not supported');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords || {};
        this.setUserCoordinates(latitude, longitude);
        console.log('[StoreSelector] User coordinates', { latitude, longitude });
      },
      (error) => {
        console.warn('[StoreSelector] Geolocation error', error);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 30000 }
    );
  };

  onModalClick(event) {
    const closeTarget = event.target.closest('[data-store-selector-close],[data-store-selector-overlay]');
    if (!closeTarget) return;
    event.preventDefault();
    this.close();
  }

  onDocumentClick(event) {
    if (!this.modal || this.modal.hidden) return;
    const target = event.target;
    if (!target) return;
    if (!this.modal.contains(target)) return;
    if (target.closest('[data-store-selector-close],[data-store-selector-overlay]')) {
      event.preventDefault();
      this.close();
    }
  }

  static bindGlobalCloseListener() {
    if (window.__storeSelectorGlobalCloseListenerBound) return;
    window.__storeSelectorGlobalCloseListenerBound = true;
    document.addEventListener('click', StoreSelectorComponent.onGlobalCloseClick, true);
  }

  static onGlobalCloseClick(event) {
    const closeTarget = event.target.closest('[data-store-selector-close],[data-store-selector-overlay]');
    if (!closeTarget) return;
    const modal = closeTarget.closest('.store-selector__modal');
    const instance = modal?.__storeSelectorInstance;
    console.debug('[StoreSelector] close click', {
      target: closeTarget,
      hasInstance: Boolean(instance),
      modalHidden: modal?.hidden,
    });
    event.preventDefault();
    if (instance && typeof instance.close === 'function') {
      instance.close();
      return;
    }
    if (modal) {
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('store-selector-open');
    }
  }

  open() {
    if (!this.modal) {
      this.modal = this.querySelector('[data-store-selector-modal]')
        || document.body.querySelector('[data-store-selector-modal]');
      if (this.modal && !this.modal.dataset.detachedFromHost) {
        this.modal.dataset.detachedFromHost = 'true';
        document.body.appendChild(this.modal);
      }
      if (this.modal) {
        // Re-cache sub-elements: cacheElements() ran before modal was available
        this.dialog = this.modal.querySelector('.store-selector__dialog');
        this.overlay = this.modal.querySelector('[data-store-selector-overlay]');
        this.closeButtons = Array.from(this.modal.querySelectorAll('[data-store-selector-close]'));
        this.optionsList = this.modal.querySelector('.store-selector__options');
        this.statusEl = this.modal.querySelector('[data-store-selector-status]');
        this.clearButton = this.modal.querySelector('[data-store-selector-clear]');
        this.searchInput = this.modal.querySelector('[data-store-selector-search]');
        this.useLocationButton = this.modal.querySelector('[data-store-selector-use-location]');
        this.refreshOptionButtons();

        if (!this.optionButtons.length) {
          // Re-read endpoint in case it wasn't on the element at connectedCallback time
          if (!this.locationsEndpoint) {
            this.locationsEndpoint = this.dataset.locationsEndpoint || '';
          }
          // If still no endpoint, borrow endpoint and cached locations from another instance
          if (!this.locationsEndpoint) {
            const donor = Array.from(document.querySelectorAll('store-selector'))
              .find(el => el !== this && el.locationsEndpoint);
            if (donor) {
              this.locationsEndpoint = donor.locationsEndpoint;
              if (donor.locations && donor.locations.length) {
                this.locations = [...donor.locations];
              }
            }
          }
          // Render already-loaded locations, or kick off a fresh load
          if (this.locations.length) {
            this.renderLocations(this.getSortedLocations());
          } else if (this.locationsEndpoint) {
            this.loadLocations();
          }
        }
      }
    }
    if (!this.modal) return;

    // Rebind option clicks to this instance on every open: buttons may have been
    // rendered by a since-disconnected instance whose listeners were detached
    this.refreshOptionButtons();
    if (this.optionButtons.length) {
      this.detachOptionListeners();
      this.attachOptionListeners(this.optionButtons);
    }
    this.modal.__storeSelectorInstance = this;
    this.modal.hidden = false;
    this.modal.removeAttribute('hidden');
    this.modal.setAttribute('aria-hidden', 'false');
    this.trigger?.setAttribute('aria-expanded', 'true');
    this.dataset.open = 'true';
    document.documentElement.classList.add('store-selector-open');
    document.addEventListener('keydown', this.handleKeydown);

    // Clear any loading/error status when opening the modal
    this.setStatus('');

    // Ensure search input is cached (it might not have been available during connectedCallback)
    if (!this.searchInput) {
      this.searchInput = this.modal.querySelector('[data-store-selector-search]');
    }

    // Initialize search input with Google Maps autocomplete if API key is available
    this.initSearchInput();

    this.focusFirstElement();
  }

  onSelectorClick(event) {
    if (event.target.closest('[data-store-selector-open]')) {
      this.open();
    }
  }

  close() {
    if (!this.modal) return;
    this.modal.__storeSelectorInstance = this;
    this.modal.hidden = true;
    this.modal.setAttribute('hidden', 'hidden');
    this.modal.setAttribute('aria-hidden', 'true');
    this.trigger?.setAttribute('aria-expanded', 'false');
    this.dataset.open = '';
    document.removeEventListener('keydown', this.handleKeydown);
    if (!document.querySelector('store-selector[data-open="true"]')) {
      document.documentElement.classList.remove('store-selector-open');
    }
    this.trigger?.focus();
  }

  onOptionClick(event) {
    const button = event.currentTarget;
    if (!button || button.disabled) return;

    const value = button.dataset.value || '';
    const label = button.dataset.label || '';
    const urlAdd = button.dataset.urlAdd || '';
    const urlRemove = button.dataset.urlRemove || '';
    const isActive = button.dataset.active === 'true';

    if (isActive) {
      this.clearSelection(urlRemove || this.dataset.clearUrl || '');
    } else {
      this.selectValue(value, label, urlAdd);
    }
  }

  onClearClick(event) {
    event.preventDefault();
    this.clearSelection(this.dataset.clearUrl || '');
  }

  async selectValue(value, label, fallbackUrl) {
    await this.applySelection(value, label, fallbackUrl);
  }

  async clearSelection(fallbackUrl) {
    // Clear localStorage fallback storage
    try {
      localStorage.removeItem('storeSelectorSelection');
    } catch (e) {
      // Ignore localStorage errors
    }
    
    // Clear cart attributes using the clearAll flag
    if (typeof window.updatePickupCartAttributes === 'function') {
      try {
        await window.updatePickupCartAttributes({ clearAll: true });
      } catch (e) {
        console.warn('[StoreSelector] Failed to clear cart attributes', e);
      }
    }
    
    await this.applySelection('', '', fallbackUrl);
  }

  async applySelection(value, label, fallbackUrl) {
    const prevValue = this.activeValue;
    const prevLabel = this.activeLabel;
    const updated = this.updateForms(value);

    if (updated) {
      this.setActiveValue(value, label);
      this.close();
      return;
    }

    const success = await this.applyLocationSelection(value, label);
    if (!success && fallbackUrl) {
      window.location.assign(fallbackUrl);
    }

    if (success) {
      this.setActiveValue(value, label);
      this.close();
    } else {
      this.setActiveValue(prevValue, prevLabel);
    }
  }

  async applyLocationSelection(value, label) {
    const targetValue = value === '' || value === null ? this.defaultLocationId : value;
    const normalizedValue = this.parseId(targetValue);
    const api = window.inventoryInfo?.api;

    // Clear any previous error status when attempting a new selection
    this.setStatus('');

    if (api?.selectLocation) {
      try {
        await api.selectLocation(normalizedValue);
        // Persist to localStorage as reliable cross-page storage
        try {
          localStorage.setItem('storeSelectorSelection', JSON.stringify({ id: String(normalizedValue), label: label || '' }));
        } catch (e) { /* ignore */ }
        // Clear status on successful selection
        this.setStatus('');
        await this.syncWithInventorySelection();
        return true;
      } catch (error) {
        if (error?.status === 422) {
          this.setStatus(this.cartErrorText);
          // Auto-clear the error message after 5 seconds
          setTimeout(() => {
            this.setStatus('');
          }, 5000);
          return false;
        }
        this.setStatus(this.errorText);
        // Auto-clear the error message after 5 seconds
        setTimeout(() => {
          this.setStatus('');
        }, 5000);
        console.error('[StoreSelector] Failed to select location via API', error);
      }
    }

    // Fallback: store locally so we can at least persist the UI state
    try {
      localStorage.setItem(
        'storeSelectorSelection',
        JSON.stringify({ id: normalizedValue, label: label || normalizedValue })
      );
      this.setActiveValue(normalizedValue, label);
      return true;
    } catch (error) {
      console.error('[StoreSelector] Failed to persist selection', error);
    }

    return false;
  }

  updateForms(nextValue) {
    if (!this.filterParam) return false;
    const forms = this.getFacetForms();
    if (!forms.length) return false;

    let changeTarget = null;
    let stateChanged = false;

    forms.forEach((form) => {
      const inputs = Array.from(form.elements).filter((element) => element.name === this.filterParam);
      if (!inputs.length) return;
      if (!changeTarget) {
        changeTarget = inputs[0];
      }
      inputs.forEach((input) => {
        const shouldBeChecked = nextValue !== null && input.value === nextValue;
        if (input.checked !== shouldBeChecked) {
          input.checked = shouldBeChecked;
          stateChanged = true;
        }
      });
    });

    if (stateChanged && changeTarget) {
      changeTarget.dispatchEvent(new Event('change', { bubbles: true }));
    }

    return stateChanged;
  }

  getFacetForms() {
    return Array.from(document.querySelectorAll('form[is="facet-form"]'));
  }

  setActiveValue(value, label) {
    this.activeValue = value === null ? '' : String(value || '');
    this.dataset.activeValue = this.activeValue;
    this.dataset.activeLabel = label || '';

    // Determine display label - never show the ID, always prefer label or placeholder
    let displayLabel = this.placeholder;
    if (this.activeValue && label) {
      displayLabel = label;
    } else if (this.activeValue && !label) {
      // If we have a value but no label, try to find the location name from locations list
      if (this.locations && this.locations.length > 0) {
        const foundLocation = this.locations.find(loc => String(loc.id) === String(this.activeValue));
        if (foundLocation?.name) {
          displayLabel = this.formatLocationLabel(foundLocation);
        } else {
          // If location not found, keep placeholder (don't show ID)
          displayLabel = this.placeholder;
        }
      } else {
        // If locations not loaded yet, keep placeholder (don't show ID)
        displayLabel = this.placeholder;
      }
    }
    
    if (this.valueTarget) {
      this.valueTarget.textContent = displayLabel || this.placeholder || '';
    }

    const hasSelection =
      this.activeValue !== '' && String(this.activeValue) !== String(this.defaultLocationId);
    if (this.clearButton) {
      this.clearButton.disabled = !hasSelection;
    }

    this.optionButtons.forEach((button) => {
      const isActive = Boolean(this.activeValue && button.dataset.value === this.activeValue);
      button.classList.toggle('is-active', isActive);
      button.dataset.active = isActive ? 'true' : 'false';
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    this.dispatchEvent(
      new CustomEvent('store-selector:change', {
        detail: { id: this.activeValue, label: label || this.activeValue },
        bubbles: true,
        composed: true,
      })
    );
  }

  setStatus(text) {
    if (!this.statusEl) return;
    if (text) {
      this.statusEl.hidden = false;
      this.statusEl.textContent = text;
    } else {
      this.statusEl.hidden = true;
    }
  }

  setLoading(isLoading) {
    if (this.dialog) {
      this.dialog.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }
    if (isLoading) {
      this.setStatus(this.loadingText);
      return;
    }

    if (this.optionButtons.length) {
      this.setStatus('');
      return;
    }

    if (this.statusEl && !this.statusEl.hidden && this.statusEl.textContent) {
      return;
    }

    this.setStatus(this.emptyText);
  }

  async loadLocations() {
    if (!this.locationsEndpoint || !this.optionsList) return;
    this.setLoading(true);

    try {
      const response = await fetch(this.locationsEndpoint, { cache: 'no-store' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      this.locations = this.filterHidden(this.normalizeLocations(data));
      this.mergeExternalCoords();

      if (!this.locations.length) {
        this.setStatus(this.emptyText);
        this.renderLocations([]);
        return;
      }

      this.renderLocations(this.getSortedLocations());
      this.setStatus('');
      this.syncWithInventorySelection();
    } catch (error) {
      console.error('[StoreSelector] Failed to load locations', error);
      this.renderLocations([]);
      this.setStatus(this.errorText);
    } finally {
      this.setLoading(false);
    }
  }

  normalizeLocations(data) {
    const list = Array.isArray(data?.locations) ? data.locations : Array.isArray(data) ? data : [];
    return list
      .map((location) => {
        const id =
          location?.id ??
          location?.locationId ??
          location?.location_id ??
          location?.location?.id ??
          null;
        const rawName = location?.name || '';
        const name = this.stripPrefix(rawName);
        const latitude = this.parseCoord(
          location?.latitude ??
            location?.lat ??
            location?.location?.latitude ??
            location?.location?.lat ??
            null
        );
        const longitude = this.parseCoord(
          location?.longitude ??
            location?.lng ??
            location?.lon ??
            location?.long ??
            location?.location?.longitude ??
            location?.location?.lng ??
            location?.location?.lon ??
            location?.location?.long ??
            null
        );
        const city = location?.city || location?.location?.city || '';
        const region =
          location?.province ||
          location?.state ||
          location?.region ||
          location?.location?.province ||
          location?.location?.state ||
          '';
        return {
          id,
          name,
          latitude,
          longitude,
          city,
          region,
        };
      })
      .filter((location) => location.id !== null && location.name);
  }

  renderLocations(locations) {
    if (!this.optionsList) return;
    this.detachOptionListeners();
    this.optionsList.innerHTML = '';

    if (!locations.length) {
      this.optionButtons = [];
      return;
    }

    const fragment = document.createDocumentFragment();
    locations.forEach((location) => {
      const item = document.createElement('li');
      const button = this.createLocationButton(location);
      item.appendChild(button);
      fragment.appendChild(item);
    });

    this.optionsList.appendChild(fragment);
    this.optionButtons = this.modal ? Array.from(this.modal.querySelectorAll('[data-store-option]')) : [];
    this.attachOptionListeners(this.optionButtons);
    this.setActiveValue(this.activeValue, this.activeLabel);
  }

  createLocationButton(location) {
    const button = document.createElement('button');
    const label = this.formatLocationLabel(location);
    
    // Calculate and format distance if user coordinates are available
    let distanceHtml = '';
    if (this.userCoordinates && 
        this.userCoordinates.latitude != null && 
        this.userCoordinates.longitude != null &&
        location.latitude != null && 
        location.longitude != null) {
      const distanceKm = this.computeDistanceKm(
        this.userCoordinates.latitude,
        this.userCoordinates.longitude,
        location.latitude,
        location.longitude
      );
      if (Number.isFinite(distanceKm)) {
        const formattedDistance = this.formatDistance(distanceKm);
        distanceHtml = `<span class="store-selector__option-distance">${formattedDistance}</span>`;
      }
    }
    
    button.type = 'button';
    button.className = 'store-selector__option btn btn--secondary';
    button.dataset.storeOption = '';
    button.dataset.value = String(location.id);
    button.dataset.label = label;
    button.dataset.active = 'false';
    button.innerHTML = `
      <span class="store-selector__option-label">
        <span class="store-selector__option-name">${label}</span>
        ${distanceHtml}
      </span>
      <span class="store-selector__option-state"></span>
    `;
    return button;
  }

  formatDistance(distanceKm) {
    // Format distance: show km for distances >= 1km, meters for < 1km
    if (distanceKm < 1) {
      const meters = Math.round(distanceKm * 1000);
      return `${meters} m`;
    } else if (distanceKm < 10) {
      // Show one decimal place for distances between 1-10km
      return `${distanceKm.toFixed(1)} km`;
    } else {
      // Show whole numbers for distances >= 10km
      return `${Math.round(distanceKm)} km`;
    }
  }

  formatLocationLabel(location) {
    return this.stripPrefix(location.name || '');
  }

  parseId(value) {
    const numberValue = Number(value);
    return Number.isNaN(numberValue) ? value : numberValue;
  }

  parseCoord(value) {
    if (value === null || value === undefined) return null;
    const num = typeof value === 'string' ? parseFloat(value) : Number(value);
    if (Number.isNaN(num) || !Number.isFinite(num)) return null;
    return num;
  }

  stripPrefix(label = '') {
    const withoutPrefix = label.replace(/^\d+\s*-\s*/, '').trim();
    return withoutPrefix.replace(/\s*\([^)]*\)\s*/g, '').trim();
  }

  getHiddenLocations() {
    const hidden = window.inventoryInfo?.settings?.hiddenLocation;
    if (!hidden || typeof hidden !== 'object') return {};
    const map = {};
    Object.keys(hidden).forEach((key) => {
      const val = hidden[key];
      if (val) {
        map[String(this.parseId(key))] = true;
      }
    });
    return map;
  }

  filterHidden(list) {
    if (!Array.isArray(list) || !Object.keys(this.hiddenLocationMap || {}).length) return list || [];
    return list.filter((loc) => !this.hiddenLocationMap[String(this.parseId(loc.id))]);
  }

  mergeExternalCoords() {
    if (!Array.isArray(this.locations) || !this.locations.length) return;
    let external = window.storeLocationCoords;
    if (!external) return;
    if (typeof external === 'string') {
      try {
        external = JSON.parse(external);
      } catch (error) {
        console.warn('[StoreSelector] Unable to parse storeLocationCoords string', error);
        return;
      }
    }

    let sourceList = [];
    if (Array.isArray(external)) {
      sourceList = external;
    } else if (Array.isArray(external?.locations)) {
      sourceList = external.locations;
    } else if (external && typeof external === 'object') {
      Object.values(external).forEach((val) => {
        if (Array.isArray(val)) {
          sourceList = sourceList.concat(val);
        } else {
          sourceList.push(val);
        }
      });
    }

    const externalCount = sourceList.length;
    const map = {};
    sourceList.forEach((item) => {
      const id =
        item?.id ??
        item?.location_id ??
        item?.locationId ??
        item?.location?.id ??
        null;
      if (id === null || id === undefined) return;
      const key = String(this.parseId(id));
      const lat = this.parseCoord(
        item?.latitude ?? item?.lat ?? item?.location?.latitude ?? item?.location?.lat ?? null
      );
      const lng = this.parseCoord(
        item?.longitude ??
          item?.lng ??
          item?.long ??
          item?.lon ??
          item?.location?.longitude ??
          item?.location?.lng ??
          item?.location?.long ??
          item?.location?.lon ??
          null
      );
      map[key] = { latitude: lat, longitude: lng };
    });

    let matchedCount = 0;
    this.locations = this.locations.map((loc) => {
      const key = String(this.parseId(loc.id));
      const coords = map[key];
      if (coords) {
        matchedCount += Number.isFinite(coords.latitude) || Number.isFinite(coords.longitude) ? 1 : 0;
        return {
          ...loc,
          latitude: coords.latitude ?? loc.latitude,
          longitude: coords.longitude ?? loc.longitude,
        };
      }
      return loc;
    });

    const withCoords = this.locations.filter(
      (loc) => Number.isFinite(loc.latitude) && Number.isFinite(loc.longitude)
    ).length;
    
  }

  getSortedLocations() {
    if (!this.userCoordinates) return [...this.locations];
    const { latitude: userLat, longitude: userLng } = this.userCoordinates || {};
    if (userLat == null || userLng == null) return [...this.locations];

    const withDistances = this.locations.map((loc) => ({
      location: loc,
      distance: this.computeDistanceKm(userLat, userLng, loc.latitude, loc.longitude),
    }));

    // Filter out locations with infinite distance
    const known = withDistances.filter((entry) => Number.isFinite(entry.distance));

    known.sort((a, b) => a.distance - b.distance);
    // Only return locations with finite distances
    return known.map((entry) => entry.location);
  }

  computeDistanceKm(lat1, lon1, lat2, lon2) {
    if (
      lat1 == null ||
      lon1 == null ||
      lat2 == null ||
      lon2 == null ||
      Number.isNaN(lat1) ||
      Number.isNaN(lon1) ||
      Number.isNaN(lat2) ||
      Number.isNaN(lon2)
    ) {
      return Number.POSITIVE_INFINITY;
    }
    const toRad = (deg) => (deg * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  setUserCoordinates(lat, lng) {
    this.userCoordinates = {
      latitude: this.parseCoord(lat),
      longitude: this.parseCoord(lng),
    };
    // Ensure we re-apply any external coords before sorting
    this.mergeExternalCoords();
    const sorted = this.getSortedLocations();
    
    this.renderLocations(sorted);
  }

  async syncWithInventorySelection() {
    const api = window.inventoryInfo?.api;
    let currentId = api?.getSelectedLocationId?.() ?? null;
    let label = this.activeLabel;

    // Fallback: if API has no selection, check localStorage
    if (currentId === undefined || currentId === null) {
      try {
        const stored = JSON.parse(localStorage.getItem('storeSelectorSelection'));
        if (stored?.id) {
          currentId = stored.id;
          label = stored.label || '';
          if (api?.selectLocation) {
            try { await api.selectLocation(Number(currentId)); } catch (e) { /* ignore */ }
          }
        }
      } catch (e) { /* ignore */ }
    }

    if (currentId === undefined || currentId === null) return;

    try {
      if (api?.getSelectedLocation) {
        try {
          const location = await api.getSelectedLocation();
          if (location?.name) {
            label = this.stripPrefix(location.name || '');
          }
        } catch (error) {
          console.warn('[StoreSelector] Failed to resolve current location details', error);
        }
      }

      // If label is still empty, try to find it from the locations list
      if (!label && this.locations && this.locations.length > 0) {
        const foundLocation = this.locations.find(loc => String(loc.id) === String(currentId));
        if (foundLocation?.name) {
          label = this.formatLocationLabel(foundLocation);
        }
      }

      // Only set active value if we have a label, otherwise use placeholder
      if (label) {
        this.setActiveValue(String(currentId), label);
      } else {
        // If no label found, don't set the value (will show placeholder)
        this.setActiveValue('', '');
      }
    } catch (error) {
      console.warn('[StoreSelector] Failed to read current location', error);
    }
  }

  initSearchInput() {
    if (!this.searchInput) {
      console.warn('[StoreSelector] Search input not found');
      return;
    }
    
    // Don't reinitialize if autocomplete is already set up
    if (this.autocomplete) {
      return;
    }
    
    // Check if Google Maps API key is available
    const apiKey = window.inventoryInfo?.settings?.geo?.apiKey;
    
    if (!apiKey) {
      console.warn('[StoreSelector] Google Maps API key not found');
      return;
    }
    
    // Load Google Maps Places API and initialize autocomplete
    this.initGoogleMapsAutocomplete(apiKey);
  }

  initGoogleMapsAutocomplete(apiKey) {
    // Check if Google Maps script is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        this.setupAutocomplete();
      }, 100);
      return;
    }

    // Load Google Maps Places API script
    const scriptId = 'google-maps-places-api';
    const existingScript = document.getElementById(scriptId);
    
    if (existingScript) {
      // Script is already loading, wait for it
      const checkGoogleMaps = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          clearInterval(checkGoogleMaps);
          setTimeout(() => {
            this.setupAutocomplete();
          }, 100);
        }
      }, 100);
      
      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkGoogleMaps);
      }, 10000);
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setTimeout(() => {
        this.setupAutocomplete();
      }, 100);
    };
    script.onerror = () => {
      console.warn('[StoreSelector] Failed to load Google Maps API');
    };
    document.head.appendChild(script);
  }

  setupAutocomplete() {
    if (!this.searchInput) {
      console.warn('[StoreSelector] Search input not available for autocomplete setup');
      return;
    }
    
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.warn('[StoreSelector] Google Maps Places API not loaded');
      return;
    }

    try {
      // Create autocomplete instance
      const autocomplete = new window.google.maps.places.Autocomplete(this.searchInput, {
        types: ['address'],
        fields: ['geometry', 'formatted_address', 'name']
      });

      // Store reference to autocomplete for cleanup if needed
      this.autocomplete = autocomplete;

      // Ensure the autocomplete dropdown appears above the modal
      // The pac-container is created by Google Maps, we need to wait for it and adjust z-index
      const adjustZIndex = () => {
        const pacContainer = document.querySelector('.pac-container');
        if (pacContainer) {
          pacContainer.style.zIndex = '2147483001';
        }
      };
      
      // Check periodically when input is focused (in case the container is created dynamically)
      let zIndexCheckInterval;
      this.searchInput.addEventListener('focus', () => {
        adjustZIndex();
        zIndexCheckInterval = setInterval(adjustZIndex, 100);
      });
      this.searchInput.addEventListener('blur', () => {
        if (zIndexCheckInterval) {
          clearInterval(zIndexCheckInterval);
        }
      });
      this.searchInput.addEventListener('input', adjustZIndex);

      // Track if a place was selected from autocomplete
      let placeSelected = false;

      // Listen for place selection
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        
        if (!place.geometry || !place.geometry.location) {
          console.warn('[StoreSelector] No geometry data for selected place');
          placeSelected = false;
          return;
        }

        placeSelected = true;

        // Get lat/long from the selected place
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        // Set user coordinates and sort locations by distance
        this.setUserCoordinates(lat, lng);
      });

      // Reset placeSelected flag when input is cleared
      this.searchInput.addEventListener('focus', () => {
        placeSelected = false;
      });
      
    } catch (error) {
      console.error('[StoreSelector] Error setting up autocomplete:', error);
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'Tab') {
      this.maintainFocus(event);
    }
  }

  maintainFocus(event) {
    const focusable = this.getFocusableElements();
    if (!focusable.length) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  getFocusableElements() {
    if (!this.dialog) return [];
    const selectors =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.from(this.dialog.querySelectorAll(selectors));
  }

  focusFirstElement() {
    const focusable = this.getFocusableElements();
    if (focusable.length) {
      focusable[0].focus();
    } else {
      this.dialog?.focus();
    }
  }
}

if (!customElements.get('store-selector')) {
  customElements.define('store-selector', StoreSelectorComponent);
}

// NOTE: Cart attribute updates for store selection are handled by BuyOnline's
// pushCartAttributes (via its own store-selector:change listener). A duplicate
// global listener here was removed to avoid redundant /cart/update.js calls
// that triggered Cloudflare rate limiting.

